import { Actions, ofType, Effect } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { MasterDataService } from 'app/profile/services/masterdata.service';
import { withLatestFrom, filter, exhaustMap, concat, catchError, map, switchMap, mergeMap } from 'rxjs/operators';
import { GET_MD_SUPPLIER_ADDRESS_CONTACT_TYPES, GetSupplierAddressContactTypesSuccess, GET_COUNTRY_LIST, GetCountryListSuccess, GET_CURRENCY, GetCurrencySuccess } from '../app.actions';
import { Store } from '@ngrx/store';
import { SelectSupplierAddressTypes, SelectCountries, SelectLanguages, SelectContactRoles, getSupplierParts, SelectToolCategories, SelectToolSubCategories, SelectEntities, SelectCurrencies, SelectDocumentTypes, SelectRegions, SelectAgencyType, SelectFormTemplate, SelectContextVerbiage, SelectTax1099Boxes } from '../selectors/masterdata.selector';
import { IncrementLoaderCount, DecrementLoaderCount } from 'app/shared/store/actions/loader.actions';
import { PushError } from 'app/shared/store/actions/error.actions';
import { MasterDataState } from '../state/masterdata.state';
import { GET_LANGUAGE_LIST, GetLanguageListSuccess, GET_CONTACT_ROLE_LIST, GetContactRoleListSuccess, GET_PART_LIST, GetPartList, GetPartListSuccess, GET_TOOL_CATEGORIES, GetToolCategoriesSuccess, GET_TOOL_SUB_CATEGORIES, GetToolSubCategories, GetToolSubCategoriesSuccess, GET_DOCUMENTTYPE, GetDocumentTypeSuccess, GET_REGION_LIST, GetRegionListSuccess, GetRegionList, GetAgencyTypes, GetAgencyTypesSuccess, GetDiversityTypesSuccess, GET_AGENCY_TYPES, GET_DIVERSITY_TYPES, GET_FORM_TEMPLATES_SUCCESS, GET_FORM_TEMPLATES, GetFormTemplate, GetFormTemplateSuccess, GET_CONTEXT_VERBIAGE, GetContextVerbiageSuccess, GET_TAX_1099_BOXES, GetTax1099BoxesSuccess, } from '../actions/masterdata.action';
import { SupplierPart } from 'app/profile/models/masterdata.model';
import { of } from 'rxjs';
import { OnboardingService } from 'app/profile/services/onboarding.service';

@Injectable()
export class MasterDataEffects {

  constructor(private masterdataService: MasterDataService,
    private actions$: Actions,
    private store: Store<MasterDataState>,
    private onboardingService: OnboardingService,) {
  }

  @Effect()
  loadSupplierContactTypes$ = this.actions$
    .pipe(
      ofType(GET_MD_SUPPLIER_ADDRESS_CONTACT_TYPES),
      withLatestFrom(this.store.select(SelectSupplierAddressTypes)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getSupplierContactTypes()
          .pipe(
            switchMap((data) => {
              return [
                new GetSupplierAddressContactTypesSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })

          )
      })
    );

  // Get Country List
  @Effect()
  loadCountries$ = this.actions$
    .pipe(
      ofType(GET_COUNTRY_LIST),
      withLatestFrom(this.store.select(SelectCountries)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getCountries()
          .pipe(
            switchMap((data) => {
              return [
                new GetCountryListSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })

          )
      })
    );

  // Get Language List
  @Effect()
  loadLanguages$ = this.actions$
    .pipe(
      ofType(GET_LANGUAGE_LIST),
      withLatestFrom(this.store.select(SelectLanguages)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getLanguages()
          .pipe(
            switchMap((data) => {
              return [
                new GetLanguageListSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })

          )
      })
    );

  @Effect()
  loadRegions$ = this.actions$
    .pipe(
      ofType(GET_REGION_LIST),
      map((action: GetRegionList) => action),
      withLatestFrom(this.store.select(SelectRegions)),
      filter(([, state]) => state == null),
      exhaustMap(([action]) => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getRegions(action.payload)
          .pipe(
            switchMap((data) => {
              return [
                new GetRegionListSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })

          )
      })
    );
  // Get Supplier Part List
  @Effect()
  loadSupplierParts$ = this.actions$
    .pipe(
      ofType(GET_PART_LIST),
      map((action: GetPartList) => action),
      withLatestFrom(this.store.select(getSupplierParts)),
      filter(([, state]) => state == null),
      exhaustMap(([action]) => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getSupplierParts(action.payload)
          .pipe(
            switchMap((res: SupplierPart[]) => {
              return [
                new GetPartListSuccess(res),
                new DecrementLoaderCount()
              ]
            }),
            catchError(err => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })
          )
      })
    );
  // Get Country List
  @Effect()
  loadContactRoles$ = this.actions$
    .pipe(
      ofType(GET_CONTACT_ROLE_LIST),
      withLatestFrom(this.store.select(SelectContactRoles)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getSupplierContactRoles()
          .pipe(
            switchMap((data) => {
              return [
                new GetContactRoleListSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })

          )
      })
    );


  @Effect()
  loadToolCategories$ = this.actions$
    .pipe(
      ofType(GET_TOOL_CATEGORIES),
      withLatestFrom(this.store.select(SelectToolCategories)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getCategories()
          .pipe(
            switchMap((data) => {
              return [
                new GetToolCategoriesSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })

          )
      })
    );

  @Effect()
  loadToolSubCategories$ = this.actions$
    .pipe(
      ofType(GET_TOOL_SUB_CATEGORIES),
      map((action: GetToolSubCategories) => action),
      withLatestFrom(this.store.select(SelectToolSubCategories)),
      filter(([action, state]) => action.forceReload || state == null || !state[action.payload]),
      exhaustMap(([action]) => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getSubcategories(action.payload, 0)
          .pipe(
            switchMap((subCategories) => {
              return [
                new GetToolSubCategoriesSuccess(subCategories, action.payload),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })
          )
      })
    );

  // Get Currencies List
  @Effect()
  loadCurrencies$ = this.actions$
    .pipe(
      ofType(GET_CURRENCY),
      withLatestFrom(this.store.select(SelectCurrencies)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getCurrencies()
          .pipe(
            switchMap((data) => {
              return [
                new GetCurrencySuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })
          )
      })
    );

  // Get DocumentType List
  @Effect()
  loadDocumentTypes$ = this.actions$
    .pipe(
      ofType(GET_DOCUMENTTYPE),
      withLatestFrom(this.store.select(SelectDocumentTypes)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getDocumentTypes()
          .pipe(
            switchMap((data) => {
              return [
                new GetDocumentTypeSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })
          )
      })
    );

  @Effect()
  LoadAgency$ = this.actions$
    .pipe(
      ofType(GET_AGENCY_TYPES),
      map((action: GetAgencyTypes) => action.payload),
      withLatestFrom(this.store.select(SelectAgencyType)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());

        return this.masterdataService.getAgencyDocumentTypes()
          .pipe(
            switchMap((data) => {
              return [
                new GetAgencyTypesSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })
          )
      }
      )
    );

  @Effect()
  LoadDiversity$ = this.actions$
    .pipe(
      ofType(GET_DIVERSITY_TYPES),
      map((action: GetDiversityTypesSuccess) => action.payload),
      withLatestFrom(this.store.select(SelectAgencyType)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getDiversityTypes()
          .pipe(
            switchMap((data) => {
              return [
                new GetDiversityTypesSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })
          )
      }

      )
    );

  @Effect()
  getFormTemplate$ = this.actions$
    .pipe(
      ofType(GET_FORM_TEMPLATES),
      map((action: GetFormTemplate) => action),
      withLatestFrom(this.store.select(SelectFormTemplate)),
      filter(([action, state]) =>  state == null || !state[action.payload]),
      exhaustMap(([action]) => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getFormTemplate(action.version, action.payload)
          .pipe(
            switchMap((template) => {
              return [
                new GetFormTemplateSuccess(template),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })
          )
      })
    );

  @Effect()
  loadContextVerbiage$ = this.actions$
    .pipe(
      ofType(GET_CONTEXT_VERBIAGE),
      withLatestFrom(this.store.select(SelectContextVerbiage)),
      filter(([, state]) => state == null),
      exhaustMap(() => {
        this.store.dispatch(new IncrementLoaderCount());
        return this.masterdataService.getContextVerbiage()
          .pipe(
            switchMap((data) => {
              return [
                new GetContextVerbiageSuccess(data),
                new DecrementLoaderCount()
              ]
            }),
            catchError((err) => {
              return [
                new PushError(err),
                new DecrementLoaderCount()
              ]
            })
          )
      })
    );

    @Effect()
    loadTax1099Boxes$ = this.actions$
        .pipe(
            ofType(GET_TAX_1099_BOXES),
            withLatestFrom(this.store.select(SelectTax1099Boxes)),
            filter(([, state]) => state == null),
            exhaustMap(() => {
              this.store.dispatch(new IncrementLoaderCount());
              return this.masterdataService.getTax1099Box()
                .pipe(
                  switchMap((data) => {
                    return [
                      new GetTax1099BoxesSuccess(data),
                      new DecrementLoaderCount()
                    ]
                  }),
                  catchError((err) => {
                    return [
                      new PushError(err),
                      new DecrementLoaderCount()
                    ]
                  })
                )
            })
        );
}

